(() => {
  const selector = '[data-cart-tier-rewards]';
  let refreshTimer;
  let isUpdating = false;
  let syncedThemeId;
  let syncedProductCouponKey;
  let previousMilestone = 0;

  const showCelebration = (drawer, discount, message) => {
    if (!discount || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const celebrationHost = drawer.querySelector('.drawer__inner') || drawer;
    celebrationHost.querySelector('.cart-tier-celebration')?.remove();
    const celebration = document.createElement('div');
    celebration.className = 'cart-tier-celebration';
    celebration.innerHTML = `
      <div class="cart-tier-celebration__message">
        <strong>CONGRATULATIONS!</strong>
        <span>${message}</span>
      </div>
      <div class="cart-tier-celebration__confetti" aria-hidden="true">
        ${Array.from({length: 54}, (_, index) => {
          const x = (index * 37) % 96 + 2;
          const y = (index * 53) % 78 + 4;
          const driftX = ((index * 29) % 150) - 75;
          const driftY = ((index * 41) % 180) - 90;
          const rotation = (index * 47) % 360;
          const delay = (index % 12) * 45;
          return `<i style="--confetti-x:${x}%;--confetti-y:${y}%;--confetti-drift-x:${driftX}px;--confetti-drift-y:${driftY}px;--confetti-rotation:${rotation}deg;--confetti-delay:${delay}ms"></i>`;
        }).join('')}
      </div>
    `;
    celebrationHost.appendChild(celebration);
    window.setTimeout(() => celebration.remove(), 2800);
  };

  const setNodeImage = (node, item) => {
    node.classList.toggle('is-filled', Boolean(item));
    node.style.backgroundImage = item ? `url("${item.image}")` : '';
  };

  const syncThemeCartAttribute = async (rewards) => {
    const themeId = rewards.dataset.cartTierThemeId;
    const discountType = rewards.dataset.discountType || 'percentage';
    const percentageDiscount = rewards.dataset.percentageDiscount || '5';
    const fixedDiscount = rewards.dataset.fixedDiscount || '0';
    const syncKey = `${themeId}:${discountType}:${percentageDiscount}:${fixedDiscount}`;
    if (!themeId || syncedThemeId === syncKey) return;

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      if (
        cart.attributes?.cart_tier_rewards_theme_id !== themeId ||
        cart.attributes?.cart_tier_rewards_discount_type !== discountType ||
        cart.attributes?.cart_tier_rewards_percentage_discount !== percentageDiscount ||
        cart.attributes?.cart_tier_rewards_fixed_discount !== fixedDiscount
      ) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({attributes: {
            cart_tier_rewards_theme_id: themeId,
            cart_tier_rewards_discount_type: discountType,
            cart_tier_rewards_percentage_discount: percentageDiscount,
            cart_tier_rewards_fixed_discount: fixedDiscount,
          }}),
        });
      }
      syncedThemeId = syncKey;
    } catch (error) {
      console.error('Unable to synchronize the cart tier reward theme marker.', error);
    }
  };

  const applyEligibleProductCoupons = async (rewards, qualifiesForTierReward) => {
    if (!qualifiesForTierReward || rewards.dataset.productCouponsEnabled !== 'true') return;

    let mappings;
    try {
      mappings = JSON.parse(rewards.dataset.productCouponMappings || '[]');
    } catch (error) {
      console.error('Unable to read cart tier product coupon settings.', error);
      return;
    }
    if (!mappings.length) return;

    try {
      const response = await fetch('/cart.js', {credentials: 'same-origin'});
      const cart = await response.json();
      const productIds = new Set(cart.items.map((item) => String(item.product_id)));
      const codes = [...new Set(mappings
        .filter((mapping) => productIds.has(String(mapping.productId)))
        .map((mapping) => mapping.code.trim())
        .filter(Boolean))];
      const couponKey = codes.sort().join(',');
      if (!couponKey || couponKey === syncedProductCouponKey) return;

      // The discount endpoint requires the comma separators to remain literal.
      // Encoding the full list turns commas into %2C and Shopify treats all
      // product codes as one invalid discount code.
      const discountCodes = codes.map((code) => encodeURIComponent(code)).join(',');
      // `/discount/{coupon}` is Shopify's documented discount URL. Also write
      // the codes through Ajax Cart first so the active cart session preserves
      // them when the tier reward causes the drawer to re-render.
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({discount: couponKey}),
      });
      const discountResponse = await fetch(`/discount/${discountCodes}?redirect=${encodeURIComponent('/cart')}`, {
        credentials: 'same-origin',
        redirect: 'follow',
        cache: 'no-store',
      });
      if (!discountResponse.ok) throw new Error(`Discount endpoint returned ${discountResponse.status}`);

      // Re-render the drawer from Shopify after the discount session changes.
      // This makes the new code and recalculated totals visible without reload.
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionIds = cartDrawer.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsResponse = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(sectionIds)}`, {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        const sections = await sectionsResponse.json();
        cartDrawer.renderContents({id: 'cart-tier-reward-coupon', sections});
      }
      syncedProductCouponKey = couponKey;
    } catch (error) {
      console.error('Unable to apply eligible cart tier product coupons.', error);
    }
  };

  const updateRewards = async () => {
    if (isUpdating) return;

    const rewards = document.querySelector(selector);
    const drawer = document.querySelector('cart-drawer');
    if (!rewards || !drawer) return;

    isUpdating = true;
    // First persist the tier settings so Shopify calculates the Function
    // discount. Apply the product code only after that cart update completes;
    // applying both in parallel can cause Shopify to discard the code.
    await syncThemeCartAttribute(rewards);
    const lines = [...drawer.querySelectorAll('[data-cart-tier-line]')].map((line) => ({
      appliance: line.dataset.cartTierAppliance === 'true',
      quantity: Number(line.dataset.cartTierQuantity),
      linePrice: Number(line.dataset.cartTierLinePrice),
      image: line.querySelector('.cart-item__image')?.currentSrc || line.querySelector('.cart-item__image')?.src || '',
      title: line.querySelector('.cart-item__name')?.textContent.trim() || 'Cart product',
    }));
    const appliances = lines.filter((line) => line.appliance);
    const nonAppliances = lines.filter((line) => !line.appliance);
    const chronologicalAppliances = [...appliances].reverse();
    const chronologicalNonAppliances = [...nonAppliances].reverse();
    const applianceQuantity = appliances.reduce((total, line) => total + line.quantity, 0);
    const nonApplianceQuantity = nonAppliances.reduce((total, line) => total + line.quantity, 0);
    const discountType = rewards.dataset.discountType || 'percentage';
    const percentageDiscount = Number(rewards.dataset.percentageDiscount) || 0;
    const fixedDiscount = Number(rewards.dataset.fixedDiscount) || 0;
    const rewardLabel = discountType === 'fixed'
      ? `${new Intl.NumberFormat('en-IN', {style: 'currency', currency: 'INR', maximumFractionDigits: 0}).format(fixedDiscount)} OFF`
      : `${percentageDiscount}% OFF`;
    const rewardDescription = discountType === 'fixed'
      ? new Intl.NumberFormat('en-IN', {style: 'currency', currency: 'INR', maximumFractionDigits: 0}).format(fixedDiscount)
      : `${percentageDiscount}%`;
    const timelineNodes = rewards.querySelectorAll('[data-cart-tier-node]');
    const title = rewards.querySelector('[data-cart-tier-title]');
    const message = rewards.querySelector('[data-cart-tier-message]');
    const progressSegments = rewards.querySelectorAll('[data-cart-tier-segment]');
    const journeyMessage = rewards.querySelector('[data-cart-tier-journey-message]');
    const milestoneLabel = rewards.querySelector('[data-cart-tier-milestone-label]');
    let discount = 0;
    let nodeItems = [undefined];
    let titleText = rewards.dataset.heading;
    let messageText = '';
    let journeyText = 'Add an appliance to start unlocking cart rewards.';

    if (applianceQuantity >= 2) {
      discount = discountType === 'fixed' ? fixedDiscount : percentageDiscount;
      nodeItems = [chronologicalAppliances[0]];
      titleText = 'CONGRATULATIONS!';
      messageText = `${rewardDescription} off unlocked with two appliances.`;
      journeyText = `${rewardDescription} tier reward applied to your cart.`;
    } else if (nonApplianceQuantity >= 2) {
      discount = discountType === 'fixed' ? fixedDiscount : percentageDiscount;
      nodeItems = [chronologicalNonAppliances[0], undefined];
      titleText = 'CONGRATULATIONS!';
      messageText = `${rewardDescription} off unlocked with two add-ons.`;
      journeyText = `${rewardDescription} tier reward applied to your cart.`;
    } else if (appliances.length >= 1 && nonAppliances.length >= 1) {
      discount = discountType === 'fixed' ? fixedDiscount : percentageDiscount;
      nodeItems = [chronologicalNonAppliances[0], undefined];
      titleText = 'CONGRATULATIONS!';
      messageText = `${rewardDescription} off unlocked with your appliance and add-on.`;
      journeyText = `${rewardDescription} tier reward applied to your cart.`;
    } else if (appliances.length >= 1) {
      nodeItems = [chronologicalAppliances[0]];
      messageText = `Add any item to get ${rewardDescription} off.`;
      journeyText = `Add another item to get ${rewardDescription} off.`;
    } else {
      messageText = 'Add an appliance to start unlocking cart rewards.';
      nodeItems = [undefined];
    }

    applyEligibleProductCoupons(rewards, Boolean(discount));
    rewards.classList.toggle('is-unlocked', Boolean(discount));
    if (title.textContent !== titleText) title.textContent = titleText;
    if (message.textContent !== messageText) message.textContent = messageText;
    if (journeyMessage.textContent !== journeyText) journeyMessage.textContent = journeyText;
    if (milestoneLabel?.textContent !== rewardLabel) milestoneLabel.textContent = rewardLabel;
    timelineNodes.forEach((node, index) => setNodeImage(node, nodeItems[index]));
    progressSegments[0]?.classList.toggle('is-filled', Boolean(discount));
    progressSegments[0]?.classList.toggle('is-partial', !discount && (applianceQuantity >= 1 || nonApplianceQuantity >= 1));

    if (discount > previousMilestone) {
      showCelebration(drawer, discount, `${rewardDescription} reward unlocked`);
    }
    previousMilestone = discount;

    isUpdating = false;
  };

  const scheduleUpdate = () => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(updateRewards, 0);
  };

  const scheduleDrawerUpdate = () => {
    window.requestAnimationFrame(scheduleUpdate);
  };

  document.addEventListener('DOMContentLoaded', () => {
    scheduleUpdate();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      // Limit DOM observation to drawer replacements rather than every page mutation.
      new MutationObserver(scheduleDrawerUpdate).observe(drawer, {childList: true, subtree: true});
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleDrawerUpdate);
  }
})();